import type {
  ClipboardAgentSearchDetail,
  ClipboardTree,
} from "@/features/clipboard/api/clipboard";
import {
  sortMergedRows,
  type ListRow,
  type SortColumn,
  type SortDirection,
} from "@/features/clipboard/utils/sortListing";
import { useEffect, useMemo, useRef, useState } from "react";

/** Rows rendered up front / added per scroll step; keeps huge clipboards snappy. */
const RENDER_CHUNK = 120;

type UseClipboardListingOptions = {
  tree: ClipboardTree | undefined;
  activeSearch: ClipboardAgentSearchDetail | undefined;
  routeSearchId: string | undefined;
  path: string;
  sharedOwnerId: string | null;
};

/** The list no longer scrolls on its own — the page does. Find the nearest
 * scrolling ancestor so "back to top" and the render-more sentinel track it. */
const scrollParentOf = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

export const useClipboardListing = ({
  tree,
  activeSearch,
  routeSearchId,
  path,
  sharedOwnerId,
}: UseClipboardListingOptions) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>("updated");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [visibleCount, setVisibleCount] = useState(RENDER_CHUNK);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    setSortColumn(routeSearchId ? "relevance" : "updated");
    setSortDirection("desc");
  }, [routeSearchId]);

  // One merged listing: folders rank by their newest contained file, so an
  // active folder surfaces above stale loose files instead of folders always
  // pinning to the top.
  const rows = useMemo<ListRow[]>(() => {
    if (!tree && !activeSearch) return [];
    return sortMergedRows(
      activeSearch?.folders ?? tree?.folders ?? [],
      activeSearch?.documents ?? tree?.documents ?? [],
      sortColumn,
      sortDirection,
    );
  }, [tree, activeSearch, sortColumn, sortDirection]);

  // Progressive rendering: only mount rows near the viewport; a sentinel row
  // extends the window as the user scrolls. Thousands of entries stay cheap.
  useEffect(() => {
    setVisibleCount(RENDER_CHUNK);
    scrollParentOf(listScrollRef.current)?.scrollTo({ top: 0 });
  }, [path, routeSearchId, sortColumn, sortDirection, sharedOwnerId]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || visibleCount >= rows.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((n) => Math.min(n + RENDER_CHUNK, rows.length));
        }
      },
      { root: scrollParentOf(listScrollRef.current), rootMargin: "600px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, rows.length]);

  const visibleRows = rows.slice(0, visibleCount);

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === "updated" ? "desc" : "asc");
  };

  const ariaSort = (column: SortColumn): "ascending" | "descending" | "none" =>
    sortColumn === column
      ? sortDirection === "asc"
        ? "ascending"
        : "descending"
      : "none";

  return {
    sortColumn,
    sortDirection,
    rows,
    visibleRows,
    visibleCount,
    toggleSort,
    ariaSort,
    listScrollRef,
    sentinelRef,
  };
};
